// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect } from "react";
import { Button } from "../components/ui/button";
import { ChatCircleText, FileText, ShieldCheck, Sparkle, WarningCircle } from "@phosphor-icons/react";
import { useLocation } from "react-router-dom";
import { setToken } from "../lib/api";

export default function Login() {
  const location = useLocation();
  const error = location.state?.error;

  // If we're back on /login, any localStorage session token is stale — wipe it so
  // the next Google sign-in exchanges cleanly.
  useEffect(() => { setToken(null); }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F9FAFB]">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0F172A] text-white">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-white/10 grid place-items-center">
            <FileText size={22} weight="duotone" />
          </div>
          <div>
            <div className="font-[Manrope] font-extrabold tracking-tight text-lg">Doctrack</div>
            <div className="text-xs text-white/60 font-mono">document · communication · hub</div>
          </div>
        </div>
        <div className="space-y-6">
          <h1 className="font-[Manrope] font-extrabold text-5xl leading-[1.05] tracking-tight">
            One inbox.<br/>Every client.<br/><span className="text-emerald-400">Every document.</span>
          </h1>
          <p className="text-white/70 leading-relaxed max-w-md">
            A shared WhatsApp inbox, AI-labeled communication, document tracking with DEXT forwarding,
            and automated daily flash reports — all in one crisp workspace.
          </p>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            {[
              { icon: ChatCircleText, label: "Shared inbox" },
              { icon: Sparkle, label: "AI sentiment & urgency" },
              { icon: FileText, label: "DEXT forwarding" },
              { icon: ShieldCheck, label: "Emergent Auth" },
            ].map((it, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-white/80">
                <it.icon size={18} weight="regular" />{it.label}
              </div>
            ))}
          </div>
        </div>
        <div className="text-white/40 text-xs font-mono">© Doctrack · v0.1</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="lg:hidden flex items-center gap-2 mb-8">
              <FileText size={22} weight="duotone" />
              <div className="font-[Manrope] font-extrabold tracking-tight text-lg">Doctrack</div>
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono mb-3">Sign in</div>
            <h2 className="font-[Manrope] font-extrabold text-4xl tracking-tight text-slate-900">
              Welcome back.
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Continue with your Google account to access the Document Tracker & Communication Hub.
            </p>
          </div>

          {error ? (
            <div
              data-testid="login-error"
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              <WarningCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-red-600" />
              <span>{error}</span>
            </div>
          ) : null}

          <Button
            data-testid="login-google-btn"
            onClick={handleLogin}
            className="w-full h-12 bg-[#0F172A] text-white hover:bg-[#0F172A]/90 hover:-translate-y-[1px] hover:shadow-md transition-[transform,box-shadow,background-color] duration-200"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2">
              <path fill="#fff" d="M21.6 12.23c0-.72-.06-1.4-.18-2.06H12v3.9h5.4c-.23 1.25-.93 2.31-1.98 3.02v2.5h3.2c1.87-1.72 2.98-4.26 2.98-7.36z"/>
              <path fill="#fff" opacity=".85" d="M12 22c2.7 0 4.96-.9 6.62-2.41l-3.2-2.5c-.9.6-2.03.95-3.42.95-2.63 0-4.86-1.77-5.66-4.16H3.03v2.61A9.99 9.99 0 0 0 12 22z"/>
              <path fill="#fff" opacity=".7" d="M6.34 13.88a5.99 5.99 0 0 1 0-3.76V7.51H3.03a9.99 9.99 0 0 0 0 8.98l3.31-2.61z"/>
              <path fill="#fff" opacity=".5" d="M12 5.98c1.47 0 2.79.51 3.83 1.5l2.86-2.86A9.98 9.98 0 0 0 12 2 9.99 9.99 0 0 0 3.03 7.51l3.31 2.61C7.14 7.75 9.37 5.98 12 5.98z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="text-xs text-slate-500 font-mono">
            By continuing, you accept the workspace terms. Only authorized team accounts will be granted access.
          </div>
        </div>
      </div>
    </div>
  );
}
